import { FC } from 'react'
import { usePathname, useRouter } from 'next/navigation'

import { useMember, useTenant } from '@rewardops/auth'
import { useLocale } from '@rewardops/ui'
import {
  AnalyticEvents,
  getItemCustomLabel,
  ResponseItem,
} from '@rewardops/utils'

import { handleEnterKeyPress } from '~/utils/event-handler-utils'
import { getEarnRate } from '~/utils/item-utils'
import { useAnalytics } from '~/components/hooks/use-analytics'
import { useEarnStatement } from '~/components/hooks/use-earn-statement'
import { useMemberUUID } from '~/components/hooks/use-member-uuid'
import ResponsiveComponent from '~/components/responsive-component'
import { CardSize } from '~/app/common/enums/card-size-type'
import { FavouritesWishlistHeart } from '../../components/favourite-wishlist-heart/favourite-wishlist-heart'
import { LabelBadge } from '../../components/label-badge'

type RetailerCardProps = {
  item: ResponseItem
  position?: number
  listName?: string
  matchItemCardDimensions?: boolean // When true, matches ItemCard dimensions for branded category pages
  size?: CardSize
}

const RetailerCard: FC<RetailerCardProps> = ({
  item,
  position,
  listName,
  matchItemCardDimensions = false,
  size = CardSize.Large,
}) => {
  const tenant = useTenant()

  const { getEarnStatement } = useEarnStatement()
  const { locale } = useLocale()
  const member = useMember()
  const memberUUID = useMemberUUID()

  const { hasVariableEarn, earnRate, flatEarnRate } = getEarnRate(
    item,
    tenant?.defaultPoints,
  )

  const { push } = useRouter()

  const analytics = useAnalytics()
  const pathname = usePathname()

  const {
    summary: earnStatement,
    screenReaderSummary: screenReaderEarnStatement,
  } = getEarnStatement({
    flatEarnRate,
    earnMultiplier: earnRate,
    hasVariableCategoryEarnRate: hasVariableEarn,
  })

  const customLabel = getItemCustomLabel(item)

  const isOnSale = (item?.supplier_items[0]?.promotion_labels?.length ?? 0) >= 1

  const redirectToDetails = () => {
    const event = {
      type: AnalyticEvents.SelectRetailer,
      data: {
        member_id: memberUUID ?? 'GUEST',
        index: Number(position ?? 0) + 1,
        item_list_id:
          pathname?.split(locale ?? tenant?.locale?.default)?.[1] ?? '',
        item_list_name: listName ?? '',
        items: [
          {
            item_id: item.id.toString(),
            item_name: item.name,
            item_brand: item?.brands[0]?.name,
            item_category: item?.categories?.[0]?.path ?? '',
            status: item?.supplier_items?.[0]?.custom_properties?.find(
              (property) => property?.key == 'featured',
            )
              ? 'featured'
              : 'regular',
            price: Number(
              item?.supplier_items[0]?.retail_price?.[tenant.defaultCurrency] ||
                0,
            ),
          },
        ],
      },
    }

    analytics.sendCustomGoogleAnalyticsEvent(event)

    const brandLabel = item?.brands?.[0]?.key
    push(`/${locale}/retailers/${brandLabel}`)
  }

  const promotionBadge = customLabel && (
    <div>
      <LabelBadge label={customLabel} />
    </div>
  )

  const cardSizeMap = {
    [CardSize.Large]: 'h-[235px] w-[261px]',
    [CardSize.Medium]: 'h-[235px] w-[204px]',
    [CardSize.Small]: 'h-[235px] w-[163px]',
  }

  // Styling based on whether we should match ItemCard dimensions
  const cardClassName = matchItemCardDimensions
    ? 'grid grid-rows-[auto_1fr] md:h-[350px] h-[331px] cursor-pointer md:rounded-lg sm:rounded-none border border-zinc-200 hover:shadow-[0_3px_8px_0px_rgba(0,0,0,0.2)]'
    : `${
        cardSizeMap?.[size] ?? 'h-[235px] w-[261px]'
      } cursor-pointer rounded-lg border border-[#DEE2E7] hover:shadow-[0_3px_8px_0px_rgba(0,0,0,0.2)]`

  const imageContainerClassName = matchItemCardDimensions
    ? 'flex items-center justify-center px-6 pt-7'
    : 'h-[136px] p-6'

  const imageWrapperClassName = matchItemCardDimensions
    ? 'h-[156px] w-full lg:h-[180px] lg:w-[180px]'
    : 'h-full w-full'

  const contentContainerClassName = matchItemCardDimensions
    ? 'flex flex-col gap-1.5 px-3 pb-6 pt-0 md:px-6 md:pt-6'
    : 'h-auto px-6 pb-6'

  const titleClassName = matchItemCardDimensions
    ? 'line-clamp-2 text-base font-normal leading-6 text-gray-900'
    : 'line-clamp-1 text-base font-normal'

  const desktopComponent = (
    <div className="relative">
      <div
        className={`absolute right-0 top-0 ${
          matchItemCardDimensions ? '' : 'mr-2 mt-2'
        }`}
      >
        <FavouritesWishlistHeart item={item} />
      </div>
      <div
        aria-label={`${item.name} ${screenReaderEarnStatement}`}
        onClick={redirectToDetails}
        tabIndex={0}
        onKeyDown={(e) => handleEnterKeyPress(e, redirectToDetails)}
        className={cardClassName}
      >
        <div className={imageContainerClassName}>
          <div className={imageWrapperClassName}>
            <img
              className="h-full w-full object-contain"
              src={item?.images?.[0]?.url}
              alt={`${item?.name}-brand-image`}
            />
          </div>
        </div>
        <div className={contentContainerClassName}>
          <div
            className={
              matchItemCardDimensions ? 'flex flex-col' : 'flex flex-col'
            }
          >
            {matchItemCardDimensions ? (
              <h3 className={titleClassName}>{item?.name}</h3>
            ) : (
              <div className={titleClassName}>{item?.name}</div>
            )}
            <div className={matchItemCardDimensions ? 'space-y-1.5' : ''}>
              <div
                className={`text-lg font-semibold ${
                  isOnSale ? 'text-promotion' : ''
                }`}
              >
                {earnStatement}
              </div>
              {promotionBadge}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  // Mobile styling based on whether we should match ItemCard dimensions
  const mobileCardClassName = matchItemCardDimensions
    ? 'grid grid-rows-[auto_1fr] h-[331px] cursor-pointer sm:rounded-none border border-zinc-200 hover:shadow-[0_3px_8px_0px_rgba(0,0,0,0.2)]'
    : 'h-[235px] cursor-pointer border border-[#DEE2E7]'

  const mobileImageContainerClassName = matchItemCardDimensions
    ? 'flex items-center justify-center px-6 pt-7'
    : 'h-[136px] p-6'

  const mobileImageWrapperClassName = matchItemCardDimensions
    ? 'h-[156px] w-full'
    : 'h-full w-full'

  const mobileContentContainerClassName = matchItemCardDimensions
    ? 'flex flex-col gap-1.5 px-3 pb-6 pt-0'
    : 'h-auto px-3 pb-6'

  const mobileTitleClassName = matchItemCardDimensions
    ? 'line-clamp-2 text-base font-normal leading-6 text-gray-900'
    : 'line-clamp-1 text-base font-normal'

  const mobileComponent = (
    <div className="relative">
      <div
        className={`absolute right-0 top-0 ${
          matchItemCardDimensions ? '' : 'mr-2 mt-2'
        }`}
      >
        <FavouritesWishlistHeart item={item} />
      </div>

      <div
        aria-label={`${item.name} ${screenReaderEarnStatement}`}
        onClick={redirectToDetails}
        tabIndex={0}
        onKeyDown={(e) => handleEnterKeyPress(e, redirectToDetails)}
        className={mobileCardClassName}
      >
        <div className={mobileImageContainerClassName}>
          <div className={mobileImageWrapperClassName}>
            <img
              className="h-full w-full object-contain"
              src={item?.images?.[0]?.url}
              alt={`${item?.name}-brand-image`}
            />
          </div>
        </div>
        <div className={mobileContentContainerClassName}>
          <div className="flex flex-col">
            {matchItemCardDimensions ? (
              <h3 className={mobileTitleClassName}>{item?.name}</h3>
            ) : (
              <div className={mobileTitleClassName}>{item?.name}</div>
            )}
            <div className={matchItemCardDimensions ? 'space-y-1.5' : ''}>
              <div
                className={`text-lg font-semibold ${
                  isOnSale ? 'text-promotion' : ''
                }`}
              >
                {earnStatement}
              </div>
              {promotionBadge}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <ResponsiveComponent
      desktopComponent={desktopComponent}
      mobileComponent={mobileComponent}
    />
  )
}

export { RetailerCard }
